import { Trans, useTranslation } from 'react-i18next'
import { useCallback, useState } from 'react'
import { Alert, Button } from '@/common'
import { Link } from 'react-router-dom'
import { requestResetPasswordSchema } from '../../api/AuthModel'
import { FormErrorsAlert } from '@/shared/components'
import { ZodIssue } from 'zod'
import { useRequestResetPassword } from '../../hooks/useAuth'
import toast from 'react-hot-toast'
import AuthLayout from '../../components/AuthLayout'
import { EmailInputGroup } from '@/shared/components'

export default function ForgotPasswordPage() {
  const { t } = useTranslation()
  const [email, setEmail] = useState('')
  const [errors, setErrors] = useState<ZodIssue[]>([])
  const [success, setSuccess] = useState(false)
  const { mutateAsync: requestResetPassword, isPending } = useRequestResetPassword()

  const onSubmit = useCallback(
    async (event: React.FormEvent<HTMLFormElement>) => {
      event.preventDefault()
      setErrors([])
      setSuccess(false)

      const validationResult = requestResetPasswordSchema.safeParse({ email })

      if (!validationResult.success) {
        setErrors(validationResult.error.issues)
      } else {
        try {
          await requestResetPassword({ email })
          setSuccess(true)
          toast.success(t('forgotPassword.notifications.success'))
        } catch (error) {
          toast.error(t('forgotPassword.notifications.error'))
          console.error(error)
        }
      }
    },
    [email, requestResetPassword, t]
  )

  return (
    <AuthLayout showCarousel={false}>
      <div className="flex flex-col items-center space-y-4 w-full max-w-md">
        <div className="text-center">
          <p>{t('forgotPassword.description')}</p>
        </div>
        <form onSubmit={onSubmit} className="space-y-4 w-full mb-8">
          {success && (
            <Alert type="success" className="whitespace-pre-line">
              {t('forgotPassword.success')}
            </Alert>
          )}
          <FormErrorsAlert errors={errors} stringKeysPrefix="forgotPassword.fields" />
          <EmailInputGroup
            onChange={evt => setEmail(evt.target.value)}
            value={email}
            disabled={success}
            name="email"
            label={t('forgotPassword.fields.email.label')}
          />
          <Button type="submit" className="w-full" isLoading={isPending} disabled={success}>
            {t('forgotPassword.actions.send')}
          </Button>
        </form>
        <div className="text-sm px-10 text-center">
          <Trans
            i18nKey="forgotPassword.backToLogin"
            components={{
              Link: <Link className="text-green-600 underline" to="/login" />
            }}
          />
        </div>
      </div>
    </AuthLayout>
  )
}
